import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { GraphiQLPage } from '@backstage-community/plugin-graphiql';
import { QetaPage } from '@drodil/backstage-plugin-qeta';
import { ToolboxPage } from '@drodil/backstage-plugin-toolbox';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { githubAuthApiRef } from '@backstage/core-plugin-api';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { DevToolsPage } from '@backstage/plugin-devtools';
import { customDevToolsPage } from './components/devtools/CustomDevToolsPage';
import { EntityValidationPage } from '@backstage-community/plugin-entity-validation';
import {
  NotificationsPage,
  UserNotificationSettingsCard,
} from '@backstage/plugin-notifications';
import { AnnouncementsPage } from '@backstage-community/plugin-announcements';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import {
  SettingsLayout,
  UserSettingsPage,
} from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import {
  FluxApiExplorerPage,
  FluxCatalogPage,
  FluxDocsPage,
} from './components/catalog/index-page/FluxCatalogPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { fluxLightTheme, fluxDarkTheme } from './theme';
import './theme/flux.css';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';
import { PostfluxPage } from '@internal/plugin-postflux';
import { VideosPage } from '@internal/plugin-videos';
import { ApplicationsPage } from '@internal/plugin-applications';
import { HandoffsPage } from '@internal/plugin-handoffs';
import { AiAssistantPage } from '@internal/plugin-ai-assistant';
import { StackPoliciesPage } from '@internal/plugin-stack-policies';
import { RailwayCostPage } from '@internal/plugin-railway-cost';
import { UnifiedThemeProvider } from '@backstage/theme';
import { FluxTemplateCard } from './components/scaffolder/FluxTemplateCard';
import { FluxTemplateListPage } from './components/scaffolder/FluxTemplateListPage';
import LightIcon from '@material-ui/icons/WbSunny';
import DarkIcon from '@material-ui/icons/Brightness2';

const app = createApp({
  apis,
  themes: [
    {
      id: 'flux-dark',
      title: 'Flux Dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={fluxDarkTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'flux-light',
      title: 'Flux Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={fluxLightTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        provider={{
          id: 'github-auth-provider',
          title: 'GitHub',
          message: 'Sign in using GitHub',
          apiRef: githubAuthApiRef,
        }}
      />
    ),
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />}>
      <FluxCatalogPage />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />}>
      <FluxDocsPage />
    </Route>
    <Route path="/devtools" element={<DevToolsPage />}>
      {customDevToolsPage}
    </Route>
    <Route path="/graphiql" element={<GraphiQLPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/toolbox" element={<ToolboxPage />} />
    <Route path="/announcements" element={<AnnouncementsPage />} />
    <Route path="/entity-validation" element={<EntityValidationPage />} />
    <Route path="/qeta" element={<QetaPage title="Questions" />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          headerOptions={{
            pageTitleOverride: 'Crear componente',
            title: 'Crear un nuevo componente',
            subtitle:
              'Selecciona una plantilla validada para iniciar un proyecto con los estándares de la plataforma.',
          }}
          components={{
            TemplateCardComponent: FluxTemplateCard,
            EXPERIMENTAL_TemplateListPageComponent: FluxTemplateListPage,
          }}
          contextMenu={{
            editor: true,
            actions: true,
            tasks: true,
            templatingExtensions: false,
          }}
        />
      }
    />
    <Route path="/api-docs" element={<ApiExplorerPage />}>
      <FluxApiExplorerPage />
    </Route>
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />}>
      <SettingsLayout.Route path="/notifications" title="Notifications">
        <UserNotificationSettingsCard originNames={{ 'plugin:qeta': 'Q&A' }} />
      </SettingsLayout.Route>
    </Route>
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/postflux" element={<PostfluxPage />} />
    <Route path="/videos" element={<VideosPage />} />
    <Route path="/applications/*" element={<ApplicationsPage />} />
    <Route path="/handoffs/*" element={<HandoffsPage />} />
    <Route path="/stack-policies" element={<StackPoliciesPage />} />
    <Route path="/ai" element={<AiAssistantPage />} />
    <Route path="/railway-cost" element={<RailwayCostPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <SignalsDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
